import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import {
  type AnimationAction,
  type AnimationClip,
  AnimationMixer,
  type Euler,
  type Material,
  type Object3D,
} from 'three';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';

import { isMhrScene } from '@cartwhl/3d/gltf/utils';
import {
  type PlayerAnimationSource,
  usePlayerActorScene,
  usePlayerSettings,
  usePlayerStoreApi,
  useResolvedPlayerAnimation,
} from '@cartwhl/3d/player';

import { useStore as useMotionEditorStore } from '../../motionEditorStore';
import { useStore as useRepathingStore } from '../../repathing/repathingStore';
import { removePoseMeshHighlightOverlays } from '../bonePicking/poseMeshHighlight';
import {
  createHistoryBvhComparisonGhostUrls,
  getKeyPoseComparisonGhostScopeKey,
  selectKeyPoseComparisonGhostHistoryEntry,
} from '../keyPoseComparisonGhostUtils';
import { resolveKeyPoseMotionContext } from '../keyPoseMotionUtils';
import { useStore as useKeyPoseStore } from '../keyPoseStore';
import { applyGhostSceneMaterials } from './ghostSceneRenderUtils';

interface HistoryBvhGhostProps {
  sceneRotation?: Euler;
}

interface HistoryBvhGhostScene {
  action: AnimationAction;
  clipDuration: number;
  materials: Material[];
  mixer: AnimationMixer;
  object: Object3D;
}

const HISTORY_GHOST_ACTOR_FALLBACK_ID = '__motion-editor-history-ghost__';
const HISTORY_GHOST_COLOR = '#276f20';
const HISTORY_GHOST_OPACITY = 0.28;
const HISTORY_GHOST_RENDER_ORDER = 12;

export default function HistoryBvhGhost({
  sceneRotation,
}: HistoryBvhGhostProps) {
  const motionEditorStore = useMotionEditorStore();
  const keyPoseStore = useKeyPoseStore();
  const repathingStore = useRepathingStore();
  const playerStore = usePlayerStoreApi();
  const lastFrameSyncRef = useRef<{
    frame: number;
    frameRate: number;
    ghost: HistoryBvhGhostScene;
  } | null>(null);
  const { scene, playback } = motionEditorStore.state;
  const keyPoseReferenceName =
    repathingStore.state.controlsMode === 'body'
      ? keyPoseStore.state.activeReferenceName
      : null;
  const motionContext = resolveKeyPoseMotionContext({
    scene,
    playback,
    keyPoseReferenceName,
  });
  const actor = motionContext
    ? (playback.sceneActors.find(
        (sceneActor) =>
          sceneActor.referenceName === motionContext.referenceName,
      ) ?? null)
    : null;
  const motionContextKey = getKeyPoseComparisonGhostScopeKey(motionContext);
  const selectedHistoryJobID =
    keyPoseStore.state.comparisonGhost.selectedHistoryJobID ===
    keyPoseStore.state.comparisonGhost.appliedHistoryJobID
      ? null
      : keyPoseStore.state.comparisonGhost.selectedHistoryJobID;
  const selectedEntry = keyPoseStore.state.comparisonGhost.disabled
    ? null
    : selectKeyPoseComparisonGhostHistoryEntry(
        keyPoseStore.state.generationHistory,
        selectedHistoryJobID,
        motionContext,
      );
  const ghostBvhUrls = useMemo(() => {
    if (!actor || !selectedEntry || !motionContextKey) {
      return [] as string[];
    }

    return createHistoryBvhComparisonGhostUrls(actor, selectedEntry);
  }, [
    actor,
    motionContextKey,
    selectedEntry?.jobID,
    selectedEntry?.outputBvhURL,
  ]);
  const ghostBvhUrlsKey = ghostBvhUrls.join('|');
  const ghostAnimation = useMemo<PlayerAnimationSource | undefined>(() => {
    if (!actor || ghostBvhUrls.length === 0) {
      return undefined;
    }

    return {
      type: 'sequence',
      urls: ghostBvhUrls,
      rotationOffsets: actor.rotationOffsets,
    };
  }, [actor?.rotationOffsets, ghostBvhUrls, ghostBvhUrlsKey]);
  const actorScene = usePlayerActorScene(
    actor?.referenceName ?? HISTORY_GHOST_ACTOR_FALLBACK_ID,
  );
  const { bvh } = useResolvedPlayerAnimation({ animation: ghostAnimation });
  const {
    settings: {
      playback: { frameRate },
    },
  } = usePlayerSettings();

  const ghost = useMemo(() => {
    if (!actorScene || !bvh) {
      return null;
    }

    return createHistoryBvhGhostScene({
      actorScene,
      bvhClip: bvh.clip,
      sceneRotation,
    });
  }, [actorScene, bvh, sceneRotation]);

  useFrame(() => {
    if (!ghost) {
      return;
    }

    const currentFrame = playerStore.state.transport.currentFrame;
    const lastFrameSync = lastFrameSyncRef.current;
    if (
      lastFrameSync?.ghost === ghost &&
      lastFrameSync.frame === currentFrame &&
      lastFrameSync.frameRate === frameRate
    ) {
      return;
    }

    syncHistoryBvhGhostFrame(ghost, currentFrame, frameRate);
    lastFrameSyncRef.current = {
      frame: currentFrame,
      frameRate,
      ghost,
    };
  });

  useEffect(() => {
    return () => {
      if (!ghost) {
        return;
      }

      ghost.mixer.stopAllAction();
      ghost.mixer.uncacheRoot(ghost.object);
      for (const material of ghost.materials) {
        material.dispose();
      }
    };
  }, [ghost]);

  if (!ghost) {
    return null;
  }

  return <primitive object={ghost.object} />;
}

function syncHistoryBvhGhostFrame(
  ghost: HistoryBvhGhostScene,
  currentFrame: number,
  frameRate: number,
) {
  const seconds = Math.max(currentFrame, 0) / frameRate;
  const time =
    ghost.clipDuration > 0 ? Math.min(seconds, ghost.clipDuration) : 0;

  ghost.action.time = time;
  ghost.mixer.setTime(time);
  ghost.object.updateMatrixWorld(true);
}

function createHistoryBvhGhostScene({
  actorScene,
  bvhClip,
  sceneRotation,
}: {
  actorScene: Object3D;
  bvhClip: AnimationClip;
  sceneRotation?: Euler;
}): HistoryBvhGhostScene {
  const ghostScene = SkeletonUtils.clone(actorScene) as Object3D;
  removePoseMeshHighlightOverlays(ghostScene);
  if (sceneRotation && !isMhrScene(actorScene)) {
    ghostScene.rotation.copy(sceneRotation);
  }

  const materials = applyGhostSceneMaterials(ghostScene, {
    color: HISTORY_GHOST_COLOR,
    opacity: HISTORY_GHOST_OPACITY,
    renderOrder: HISTORY_GHOST_RENDER_ORDER,
  });
  const mixer = new AnimationMixer(ghostScene);
  const action = mixer.clipAction(bvhClip, ghostScene);
  action.play();
  mixer.setTime(0);
  ghostScene.updateMatrixWorld(true);

  return {
    action,
    clipDuration: bvhClip.duration,
    materials,
    mixer,
    object: ghostScene,
  };
}
